/*
 * The Credits & Billing screen's state, shared with the classic panel.
 *
 * Two rules from TICK-100 survive intact here, because both are about money an organizer can lose
 * without noticing:
 *
 * - **The forfeit.** Trading a still-live lot in for a discount destroys credits that could still
 *   be spent. `tradeInCost` states the trade in both units — credits given up, baht received — so
 *   nobody discovers that exchange rate afterwards.
 * - **The collision.** "Credits expire on the 3rd" is a fact. "Your event ends on the 12th and your
 *   credits expire on the 3rd" is the one that can be acted on. `expiryRisk` computes the second.
 *
 * The quote is never estimated locally. The carryover discount is capped against the purchase
 * total, so a client-side guess would disagree with the offer the server actually makes.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { adminApi, ApiError } from '../../../api';
import type {
  CreditBalanceDto, CreditLedgerEntryDto, CreditLotDto, CreditQuoteDto, CreditTierDto,
  CreditTopupDto, CreditTopupItemInput,
} from '../../../api';

export interface LotExpiryRisk {
  lot: CreditLotDto;
  /** Days left, or null where the lot never expires. Negative once past. */
  daysLeft: number | null;
  /** The lot dies before the event it was bought for is over. The fact worth showing. */
  diesBeforeEvent: boolean;
}

/**
 * Which lots lapse, and which of those lapse too early to be any use.
 *
 * Only lots with credits left are considered: a drained lot's expiry costs nobody anything, and
 * listing it as a risk would bury the ones that matter.
 */
export function expiryRisk(balance: CreditBalanceDto | null): LotExpiryRisk[] {
  if (!balance) return [];
  const endsAt = balance.eventEndsAt ? new Date(balance.eventEndsAt).getTime() : null;
  return balance.expiringSoon
    .filter((lot) => lot.creditsRemaining > 0)
    .map((lot) => ({
      lot,
      daysLeft: lot.daysUntilExpiry,
      diesBeforeEvent: endsAt !== null && lot.expiresAt !== null
        && new Date(lot.expiresAt).getTime() < endsAt,
    }));
}

export interface TradeInCost {
  /** Credits still usable that trading in would close. Zero is the safe case. */
  liveCreditsLost: number;
  /** Lots being traded that are already dead — no loss, just value recovered. */
  deadLotsUsed: number;
}

/** What trading these lots in actually costs, separated from what it pays. */
export function tradeInCost(lots: readonly CreditLotDto[], tradeInIds: readonly string[]): TradeInCost {
  const chosen = lots.filter((l) => tradeInIds.includes(l.id));
  return {
    liveCreditsLost: chosen.filter((l) => l.live).reduce((sum, l) => sum + l.creditsRemaining, 0),
    deadLotsUsed: chosen.filter((l) => !l.live).length,
  };
}

/** The lots an organizer is allowed to trade in: still holding credits, eligible, not closed. */
export function eligibleForTradeIn(balance: CreditBalanceDto | null): CreditLotDto[] {
  return (balance?.lots ?? []).filter((l) => l.carryoverEligible && l.creditsRemaining > 0 && !l.closedAt);
}

/** Top-ups that have not delivered credits yet — the ones still blocking a balance from rising. */
export function pendingTopups(topups: readonly CreditTopupDto[]): CreditTopupDto[] {
  return topups.filter((t) => t.status === 'pending' || t.status === 'awaiting_verification');
}

export interface CreditsState {
  balance: CreditBalanceDto | null;
  tiers: CreditTierDto[];
  topups: CreditTopupDto[];
  statement: CreditLedgerEntryDto[];
  statementLoaded: boolean;
  risks: LotExpiryRisk[];
  loading: boolean;
  loadError: string;
  error: string;
  busy: boolean;

  picks: Record<string, number>;
  tradeIn: string[];
  quote: CreditQuoteDto | null;
  quoteError: string;
  setPick: (tierId: string, quantity: number) => void;
  toggleTradeIn: (lotId: string) => void;
  chooseOnly: (tierId: string) => void;
  resetPurchase: () => void;

  buy: () => Promise<boolean>;
  uploadSlip: (topupId: string, file: File) => Promise<void>;
  loadStatement: () => Promise<void>;
  reload: () => Promise<void>;
}

/** The server caps a pick at 50 of one tier; the stepper refuses past it rather than being refused. */
const MAX_PER_TIER = 50;

export function useCredits(token: string, eventSlug: string | undefined): CreditsState {
  const [balance, setBalance] = useState<CreditBalanceDto | null>(null);
  const [tiers, setTiers] = useState<CreditTierDto[]>([]);
  const [topups, setTopups] = useState<CreditTopupDto[]>([]);
  const [statement, setStatement] = useState<CreditLedgerEntryDto[]>([]);
  const [statementLoaded, setStatementLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [picks, setPicks] = useState<Record<string, number>>({});
  const [tradeIn, setTradeIn] = useState<string[]>([]);
  const [quote, setQuote] = useState<CreditQuoteDto | null>(null);
  const [quoteError, setQuoteError] = useState('');

  const load = useCallback(async () => {
    try {
      const [b, t, u] = await Promise.all([
        adminApi.getCredits(token, eventSlug),
        adminApi.listCreditTiers(token, eventSlug),
        adminApi.listCreditTopups(token, eventSlug),
      ]);
      setBalance(b);
      setTiers(t);
      setTopups(u);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลเครดิตไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const items = useMemo(
    () => Object.entries(picks).filter(([, q]) => q > 0).map(([tierId, quantity]) => ({ tierId, quantity })),
    [picks],
  );

  // Re-priced by the server on every change. The discount cap depends on the purchase total, so a
  // local estimate would disagree with the quote the organizer is actually offered.
  useEffect(() => {
    if (items.length === 0) { setQuote(null); setQuoteError(''); return; }
    let cancelled = false;
    (async () => {
      try {
        const q = await adminApi.quoteCredits(token, items as CreditTopupItemInput[], tradeIn, eventSlug);
        if (!cancelled) { setQuote(q); setQuoteError(''); }
      } catch (e) {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(e instanceof ApiError ? e.message : 'คำนวณราคาไม่สำเร็จ');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [items, tradeIn, token, eventSlug]);

  const setPick = useCallback((tierId: string, quantity: number) => {
    setPicks((p) => ({ ...p, [tierId]: Math.max(0, Math.min(MAX_PER_TIER, quantity)) }));
  }, []);

  const toggleTradeIn = useCallback((lotId: string) => {
    setTradeIn((cur) => (cur.includes(lotId) ? cur.filter((id) => id !== lotId) : [...cur, lotId]));
  }, []);

  const chooseOnly = useCallback((tierId: string) => setPicks({ [tierId]: 1 }), []);

  const resetPurchase = useCallback(() => {
    setPicks({});
    setTradeIn([]);
    setQuote(null);
    setQuoteError('');
  }, []);

  const buy = useCallback(async () => {
    if (items.length === 0) return false;
    setBusy(true);
    setQuoteError('');
    try {
      await adminApi.createCreditTopup(token, items as CreditTopupItemInput[], tradeIn, eventSlug);
      resetPurchase();
      await load();
      return true;
    } catch (e) {
      setQuoteError(e instanceof ApiError ? e.message : 'สร้างรายการเติมเครดิตไม่สำเร็จ');
      return false;
    } finally {
      setBusy(false);
    }
  }, [items, tradeIn, token, eventSlug, load, resetPurchase]);

  const uploadSlip = useCallback(async (topupId: string, file: File) => {
    setBusy(true);
    try {
      await adminApi.uploadCreditTopupSlip(token, topupId, file, eventSlug);
      await load();
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'อัปโหลดสลิปไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, [token, eventSlug, load]);

  const loadStatement = useCallback(async () => {
    if (statementLoaded) return;
    try {
      setStatement(await adminApi.getCreditStatement(token, eventSlug));
      setStatementLoaded(true);
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดรายการเดินบัญชีไม่สำเร็จ');
    }
  }, [token, eventSlug, statementLoaded]);

  return {
    balance, tiers, topups, statement, statementLoaded,
    risks: expiryRisk(balance),
    loading: balance === null && !loadError,
    loadError, error, busy,
    picks, tradeIn, quote, quoteError,
    setPick, toggleTradeIn, chooseOnly, resetPurchase,
    buy, uploadSlip, loadStatement, reload: load,
  };
}
